import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Drawer, Button, Select, Alert, Input, useToast } from '@dreamtree-org/twreact-ui';
import { KeyValue, StatusBadge, Money, SectionCard, QueryState } from '@/components';
import { formatDate, formatINR, errorText } from '@/lib/format';
import { useQuery } from '@/lib/useQuery';
import { useCan } from '@rbac/useCan';
import { useCrudList, useCrudWrite } from '../hooks';
import { modelName, modelSpecs, labelOf, optionsOf, MANUAL_STOCK, ageInDays } from '../lib/catalog';
import { checklistFor } from '../lib/wizard';
import { useProductTypes } from '@/lib/productTypes';
import UnitPhotos from './UnitPhotos';

/**
 * Unit detail (SELL-02): model, condition, masked serial (never the hash),
 * its listings, the latest inspection record, and a stock-state change.
 */
export default function UnitDrawer({ unit, onClose, onChanged }) {
  const nav = useNavigate();
  const can = useCan();
  const { toast } = useToast();
  const write = useCrudWrite();
  const [state, setState] = useState(unit ? unit.stock_state : '');
  useEffect(() => setState(unit ? unit.stock_state : ''), [unit]);
  // A stock lot (D60) is counted, not moved between states: the count is audited on the server.
  const lot = !!unit && unit.stock_mode === 'quantity';
  const [count, setCount] = useState(unit ? String(unit.qty_on_hand) : '');
  useEffect(() => setCount(unit ? String(unit.qty_on_hand) : ''), [unit]);
  const countValid = /^\d+$/.test(count) && unit && Number(count) >= Number(unit.qty_reserved);
  const saveCount = async () => {
    try {
      await write.update('InventoryUnit', unit.id, { qty_on_hand: Number(count) });
      toast.success('Stock counted', `${unit.internal_sku}: ${count} on hand.`);
      onChanged && onChanged();
    } catch (ex) {
      toast.error('Could not update', errorText(ex));
    }
  };

  const listings = useCrudList('Listing', { where: { unit_id: unit ? unit.id : 0 }, orderBy: { column: 'id', direction: 'desc' } }, { enabled: !!unit });
  const inspections = useQuery('merchantInspections', {}, { select: (r) => r.data, enabled: !!unit });
  const inspection = unit && inspections.data ? inspections.data.find((r) => r.sku === unit.internal_sku && r.results) : null;

  const saveState = async () => {
    try {
      await write.update('InventoryUnit', unit.id, { stock_state: state });
      toast.success('Stock state updated', `${unit.internal_sku} is now ${labelOf(state).toLowerCase()}.`);
      onChanged && onChanged();
    } catch (ex) {
      toast.error('Could not update', errorText(ex));
    }
  };

  const m = unit ? unit.ProductModel : null;
  // The unit's own checklist (its model's type); without a known type, the report's own items.
  const registry = useProductTypes();
  const typeItems = checklistFor(registry.byKey[m && m.product_type], registry.checklists);
  const inspectionItems = typeItems.length || !inspection ? typeItems : Object.keys(inspection.results || {}).map((key) => ({ key, label: key }));
  return (
    <Drawer
      isOpen={!!unit}
      onClose={onClose}
      title={unit ? modelName(m) : ''}
      description={unit ? `${unit.internal_sku} · ${modelSpecs(m)}` : ''}
      size="lg"
    >
      {unit ? (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2 items-center">
            <StatusBadge status={unit.stock_state} />
            <span className="text-sm opacity-70">In stock for {ageInDays(unit.created_at)} days</span>
          </div>
          <KeyValue
            columns={2}
            items={[
              { label: 'SKU', value: unit.internal_sku },
              lot
                ? { label: 'Stock', value: `${unit.qty_on_hand} on hand · ${unit.qty_reserved} held by orders` }
                : { label: 'Serial', value: <span className="font-mono">{unit.serial_masked || '—'}</span> },
              { label: 'Grade', value: unit.grade },
              { label: 'Condition', value: labelOf(unit.condition) },
              { label: 'Battery health', value: unit.battery_health_pct != null ? `${unit.battery_health_pct}%` : '—' },
              { label: 'Acquisition cost', value: formatINR(unit.acquisition_cost_paise) },
              { label: 'Received', value: formatDate(unit.created_at) },
              { label: 'OS', value: m && m.os },
            ]}
          />
          {unit.notes ? <p className="text-sm whitespace-pre-line opacity-80">{unit.notes}</p> : null}

          <SectionCard title="Listings">
            <QueryState query={listings}>
              {(rows) =>
                rows && rows.length ? (
                  <ul className="divide-y divide-[var(--app-border)]">
                    {rows.map((l) => (
                      <li key={l.id} className="py-2 flex items-center justify-between gap-3">
                        <button type="button" className="text-left min-w-0 hover:underline" onClick={() => nav(`/listings/${l.id}`)}>
                          <div className="font-medium truncate">{l.title}</div>
                          <div className="text-xs opacity-70"><Money paise={l.price_paise} /> · {l.warranty_months} mo warranty</div>
                        </button>
                        <StatusBadge status={l.status} />
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="text-sm opacity-70">This unit is not listed yet.</span>
                    {can('catalog', 'create') ? <Button size="sm" onClick={() => nav(`/listings/new?unit=${unit.id}`)}>Create listing</Button> : null}
                  </div>
                )
              }
            </QueryState>
          </SectionCard>

          {can('inventory', 'view') ? <UnitPhotos unitId={unit.id} /> : null}

          <SectionCard title="Inspection">
            {inspection ? (
              <div className="space-y-2">
                <div className="text-sm">
                  {inspection.passed ? <StatusBadge status="passed" label="All safety checks passed" /> : <StatusBadge status="failed" label="Safety checks failed" />}
                  <span className="ml-2 opacity-70">by {inspection.technician} · {formatDate(inspection.saved_at)}</span>
                </div>
                <ul className="grid sm:grid-cols-2 gap-1 text-sm">
                  {inspectionItems.map((i) => (
                    <li key={i.key} className="flex justify-between gap-2">
                      <span className="opacity-80">{i.label}</span>
                      <StatusBadge status={(inspection.results[i.key] || {}).result === 'pass' ? 'passed' : 'failed'} label={(inspection.results[i.key] || {}).result || '—'} size="sm" />
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              <p className="text-sm opacity-70">No inspection checklist on record for this unit (preview data).</p>
            )}
          </SectionCard>

          {can('inventory', 'update') && lot ? (
            <SectionCard title="Stock count">
              <div className="flex flex-wrap items-end gap-2">
                <div className="flex-1 min-w-[12rem]">
                  <Input
                    label="Quantity on hand"
                    type="number"
                    value={count}
                    onChange={(e) => setCount(e.target.value)}
                    error={count !== '' && !countValid ? `At least ${unit.qty_reserved} (held by open orders)` : undefined}
                    helperText="Enter the count on the shelf after a delivery or a stock take."
                  />
                </div>
                <Button onClick={saveCount} loading={write.loading} disabled={!countValid || Number(count) === Number(unit.qty_on_hand)}>Save count</Button>
              </div>
            </SectionCard>
          ) : null}
          {can('inventory', 'update') && !lot ? (
            <SectionCard title="Stock state">
              {MANUAL_STOCK[unit.stock_state] ? (
                <div className="flex flex-wrap items-end gap-2">
                  <div className="flex-1 min-w-[12rem]">
                    <Select options={optionsOf([unit.stock_state, ...MANUAL_STOCK[unit.stock_state]])} value={state} onChange={setState} closeOnSelect />
                  </div>
                  <Button onClick={saveState} loading={write.loading} disabled={state === unit.stock_state}>Save</Button>
                </div>
              ) : (
                <p className="text-sm opacity-70">
                  {unit.stock_state === 'returned' ? 'Returned by a customer: record a passing re-inspection to put it back in stock.' : 'Held by an order; its stock state follows the order.'}
                </p>
              )}
              {state === 'sold' && unit.stock_state !== 'sold' ? <Alert variant="warning">Marking a unit sold hides its listings from the storefront.</Alert> : null}
            </SectionCard>
          ) : null}
        </div>
      ) : null}
    </Drawer>
  );
}
